import React, { useState } from "react";
import NavigationBar from "./NavigationBar";
import { Search } from "lucide-react";


/* ------------------------------------------------------------------ */
/* 1.  STATIC DATA (remove once you fetch from your API / WebSocket)  */
/* ------------------------------------------------------------------ */

const events = [
  {
    id: "evt_001",
    type: "Exterior Alert",
    timestamp: "2025-04-08T14:32:10Z",
    location: "Building A - South Entrance",
    cameraId: "CAM-06",
    subject: "John Doe",
  },
  {
    id: "evt_002",
    type: "Spill Detected",
    timestamp: "2025-04-08T14:35:02Z",
    location: "Dorm Hall B - Rear",
    cameraId: "CAM-07",
  },
  {
    id: "evt_003",
    type: "Liquid Alert",
    timestamp: "2025-04-08T14:38:55Z",
    location: "Dorm Hall B - Rear",
    cameraId: "CAM-08",
  },
];

const seedTasks: Task[] = [
  {
    id: "task_001",
    type: "Real-Time Face Search",
    status: "Running",           // literal fits the union
    initiatedBy: "Officer Ramirez",
    timestamp: "2025-04-08T14:20:00Z",
    input: "Photo of missing person: Jane Smith",
    assignee: "Security",
  },
];

/* ------------------------------------------------------------------ */
/* 2.  ROUTING RULE & COLOUR TABLE                                    */
/* ------------------------------------------------------------------ */

type Role = "Nursing" | "Maintenance" | "Security";

const EVENT_TO_ROLE: Record<string, Role> = {
  "Exterior Alert": "Security",
  "Spill Detected": "Maintenance",
  "Liquid Alert": "Maintenance",
  "Fall Detected": "Nursing",
  "Real-Time Search": "Security",
};

const roleColor: Record<Role, string> = {
  Nursing: "bg-pink-500",
  Maintenance: "bg-amber-500",
  Security: "bg-indigo-500",
};

/* ------------------------------------------------------------------ */
/* 3.  TASK TYPE                                                      */
/* ------------------------------------------------------------------ */

type Task = {
  id: string;
  type: string;
  status: "Alerted" | "Running" | "Completed";
  initiatedBy: string;
  timestamp: string;
  input: string;
  assignee: Role;
};

/* ------------------------------------------------------------------ */
/* 4.  COMPONENT                                                      */
/* ------------------------------------------------------------------ */

export default function TaskDashboard() {
  /* --- initialise local state: convert events ➜ tasks, merge seed --- */
  const [tasks, setTasks] = useState<Task[]>(() => {
    const eventTasks: Task[] = events.map((ev) => ({
      id: ev.id,
      type: ev.type,
      status: "Alerted",
      initiatedBy: "System",
      timestamp: ev.timestamp,
      input: ev.subject ?? ev.location,
      assignee: EVENT_TO_ROLE[ev.type] ?? "Security",
    }));
    return [...eventTasks, ...seedTasks];
  });

  /* --- helper to update assignee when operator re-routes --- */
  const updateAssignee = (id: string, role: Role) =>
    setTasks((prev) =>
      prev.map((t) => (t.id === id ? { ...t, assignee: role } : t)),
    );

  /* ------------------------------ RENDER ---------------------------- */
  return (
    <div className="bg-black min-h-screen text-white">
      <NavigationBar />

      <div className="p-6">
        <h1 className="text-2xl font-bold mb-6">Event Dashboard</h1>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {tasks.map((task) => (
            <div
              key={task.id}
              className="bg-gray-800 p-4 rounded-lg shadow-md hover:shadow-lg transition-all"
            >
              {/* header row */}
              <div className="flex items-center gap-2 mb-2">
                <Search className="w-5 h-5" />
                <span className="font-semibold">{task.type}</span>
                <span
                  className={`ml-auto text-xs px-2 py-0.5 rounded-full ${
                    task.status === "Running"
                      ? "bg-yellow-100 text-yellow-800"
                      : task.status === "Completed"
                      ? "bg-green-100 text-green-800"
                      : "bg-gray-200 text-gray-800"
                  }`}
                >
                  {task.status}
                </span>
              </div>

              {/* meta rows */}
              <div className="text-xs text-gray-400">
                {new Date(task.timestamp).toLocaleString()}
              </div>
              <div className="text-xs mt-1">Initiated By: {task.initiatedBy}</div>
              <div className="text-xs mt-1">Input: {task.input}</div>

              {/* assignee row */}
              <div className="flex items-center justify-between mt-3">
                <span
                  className={`text-xs font-medium px-2 py-0.5 rounded ${roleColor[task.assignee]}`}
                >
                  {task.assignee}
                </span>

                <select
                    value={task.assignee}
                    onChange={(e) => updateAssignee(task.id, e.target.value as Role)}
                    className="text-xs bg-gray-700 px-2 py-1 rounded"
                >
                  {(["Nursing", "Maintenance", "Security"] as Role[]).map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                  ))}
                </select>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
